import axios from "axios";
import { message } from "antd";

import { API_BASE_URL } from "../config";
import { getFreshAccessToken, isAuthRefreshDeterministicError } from "../authRefreshOrchestrator";
import { getAccessToken, readAuthSnapshot } from "../authState";
import { getStoredActiveOrganizationId } from "../../utils/organizationScope";
import { handleVersionHeader } from "../../utils/versionCheck";
import { normalizeV3ResponseDates, serializeV3RequestDates } from "./timeContract";

const isDev = process.env.NODE_ENV === "development";

const AUTH_DEBUG_STORAGE_KEY = "flymeta.auth_debug";

function shouldLogAuthDebug(config) {
    try {
        return localStorage.getItem(AUTH_DEBUG_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function getAuthStorageDiagnostics() {
    const snapshot = readAuthSnapshot();
    const expiresAtRaw = snapshot.tokenExpiresAt;
    const expiresAt = Number.parseInt(expiresAtRaw || "", 10);
    const now = Math.floor(Date.now() / 1000);

    return {
        hasAccessToken: Boolean(snapshot.accessToken),
        hasRefreshToken: Boolean(snapshot.refreshToken),
        selectedStorage: snapshot.storageName || "none",
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
        now,
        expiresInSec: Number.isFinite(expiresAt) ? expiresAt - now : null,
        sessionEpoch: snapshot.meta?.sessionEpoch || null,
        revision: snapshot.meta?.revision || null,
    };
}

function getResponseAuthDetail(responseData) {
    const detail = responseData?.detail;
    if (typeof detail === "string") {
        return detail;
    }
    if (Array.isArray(detail)) {
        return detail.map((item) => item?.msg || item).join("; ");
    }
    return responseData?.message || null;
}

function getAxiosTransportDiagnostics(error) {
    const request = error?.request;
    return {
        errorName: error?.name,
        errorCode: error?.code,
        errorMessage: error?.message,
        requestReadyState: request?.readyState,
        requestStatus: request?.status,
        requestResponseURL: request?.responseURL,
        hasResponse: Boolean(error?.response),
    };
}

const v3ApiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
    paramsSerializer: {
        indexes: null,
    },
});

v3ApiClient.interceptors.request.use(
    (config) => {
        if (isDev) {
            const { method, url, params, data } = config;
            console.log(`API v3 请求: ${method?.toUpperCase()} ${url}`, {
                params,
                data: method !== "get" ? data : undefined,
            });
        }

        if (config.data && typeof config.data === "object" && !(config.data instanceof FormData)) {
            config.data = serializeV3RequestDates(config.data);
        }

        const authDebug = shouldLogAuthDebug(config);
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (authDebug) {
            console.info("[auth-debug] v3 request auth state", {
                method: config.method?.toUpperCase(),
                url: config.url,
                baseURL: config.baseURL,
                currentPath: window.location.pathname,
                ...getAuthStorageDiagnostics(),
                authorizationHeaderSet: Boolean(config.headers?.Authorization),
            });
        }

        const activeOrganizationId = getStoredActiveOrganizationId();
        if (activeOrganizationId !== null) {
            config.headers["X-Active-Organization-Id"] = String(activeOrganizationId);
        }

        return config;
    },
    (error) => Promise.reject(error),
);

v3ApiClient.interceptors.response.use(
    (response) => {
        if (isDev) {
            console.log(`API v3 响应: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
                status: response.status,
                data: response.data,
            });
        }

        if (response.config.responseType !== "blob" && response.config.responseType !== "arraybuffer" && response.data) {
            response.data = normalizeV3ResponseDates(response.data);
        }
        handleVersionHeader(response);
        return response;
    },
    async (error) => {
        const { response, config } = error;

        if (isDev) {
            console.error(`API v3 错误: ${config?.method?.toUpperCase()} ${config?.url}`, {
                status: response?.status,
                code: error?.code,
                message: error?.message,
                responseData: response?.data,
            });
        }

        if (config && shouldLogAuthDebug(config)) {
            console.info("[auth-debug] v3 response auth failure", {
                method: config.method?.toUpperCase(),
                url: config.url,
                status: response?.status,
                responseDetail: getResponseAuthDetail(response?.data),
                authorizationHeaderWasSet: Boolean(config.headers?.Authorization),
                ...getAxiosTransportDiagnostics(error),
                ...getAuthStorageDiagnostics(),
            });
        }

        if (response?.status === 401 && config && !config._retry) {
            config._retry = true;
            try {
                const accessToken = await getFreshAccessToken({ entrypoint: "legacy-v3-axios-401", force: true });
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${accessToken}`;
                return v3ApiClient(config);
            } catch (refreshError) {
                if (isAuthRefreshDeterministicError(refreshError)) {
                    message.error("登录已过期，请重新登录");
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    },
);

export default v3ApiClient;
